import { HttpErrorResponse } from '@angular/common/http';
import {
  Activity,
  Amenity, ICancellationPolicyOption,
  IDtoListingBlackoutTimes,
  IDtoListingBooking,
  IDtoListingLocation,
  IDtoSiteName,
  ILandType,
  IListing,
  IListingExtra,
  IListingWhere,
  IPhoto,
  IPolicy,
  ISite,
  ISubPhoto,
  Policy,
  SignalReception
} from '@curbnturf/entities';
import { createAction, props } from '@ngrx/store';

export enum ListingActionTypes {
  ListingInit = '[Listing] Listing initialize',
  CreateListing = '[Listing] Create Listing',
  CreateActivity = '[Listing] Create Activity',
  CreateAmenity = '[Listing] Create Amenity',
  CreatePolicy = '[Listing] Create Policy',
  CreateSignalReception = '[Listing] Create Signal Reception',
  CreateListingFailed = '[Listing] Create Listing Failed',
  LoadListing = '[Listing] Load Data',
  LoadListings = '[Listing] Load Listings',
  LoadListingFailed = '[Listing] Load Data Failed',
  ClearListings = '[Listing] Clear Listings',
  UpdateListing = '[Listing] Update',
  UpdateListingFailed = '[Listing] Update Failed',
  DelayedUploadListingImages = '[Listing] Upload Listing Images - delayed',
  UploadListingImage = '[Listing] Upload Listing Image',
  UploadBoondockReviewImage = '[Listing] Upload Boondock Review Image',
  BoondockReviewImageUploaded = '[Listing] Uploaded Boondock Review Image',
  BoondockReviewImageUploadFailed = '[Listing] Upload Failed Boondock Review Image',
  RemoveImageFromListing = '[Listing] Remove Image',
  RemoveListing = '[Listing] Remove Listing',
  RemoveListingFailed = '[Listing] Remove Listing Failed',
  RestoreListing = '[Listing] Restore Listing',
  RemoveActivity = '[Listing] Remove Activity',
  RemoveAmenity = '[Listing] Remove Amenity',
  RemovePolicy = '[Listing] Remove Policy',
  RemoveSignalReception = '[Listing] Remove Signal Reception',
  ListingCreated = '[Listing] Created',
  ListingImageRemoved = '[Listing] Image Removed',
  ListingLoaded = '[Listing] Data Loaded',
  ListingsLoaded = '[Listing] Data For Listings Loaded',
  ListingRemoved = '[Listing] Removed',
  ListingRestored = '[Listing] Restored',
  ListingUpdated = '[Listing] Updated',
  ListingImagesUpdated = '[Listing] Images Updated',
  EnableListing = '[Listing] Enable Listing',
  EnableAndHideListing = '[Listing] Enable and Hide Listing',
  DisableListing = '[Listing] Disable Listing',
  ListingDisabled = '[Listing] Listing Disabled',
  ListingDisabledFailed = '[Listing] Listing Disable Failed',
  ListingEnabled = '[Listing] Listing Enabled',
  ListingEnabledFailed = '[Listing] Listing Enable Failed',
  SelectListing = '[Listing] Select Listing',
  SelectListingByListing = '[Listing] Select By Listing',
  ListingSelected = '[Listing] Listing Selected',
  FavoriteListing = '[Listing] Favorite Listing',
  UnfavoriteListing = '[Listing] Unfavorite Listing',
  ListingFavorCompleted = '[Listing] Listing Favorite/Unfavorite Completed',
  ListingFavorFailed = '[Listing] Listing Favorite/Unfavorite Failed',
  ListingRecropImage = '[Listing] Listing Recrop Image',
  ListingRecropImageFailure = '[Listing] Listing Recrop Image Failure',
  UpdateSelectedListingSiteObject = '[Listing] Selected Listing Update Site Object',
  ListingUpdateDescription = '[Listing] Listing Update Name And Description',
  ListingUpdateDescriptionSuccess = '[Listing] Listing Update Name And Description Success',
  ListingUpdateDescriptionError = '[Listing] Listing Update Name And Description Error',
  ListingUpdatePropertyTypes = '[Listing] Listing Update Property Types',
  ListingUpdatePropertyTypesSuccess = '[Listing] Listing Update Property Types Success',
  ListingUpdatePropertyTypesError = '[Listing] Listing Update Property Types Error',
  ListingUpdatePotentialHazards = '[Listing] Listing Update Potential Hazards',
  ListingUpdatePotentialHazardsSuccess = '[Listing] Listing Update Potential Hazards Success',
  ListingUpdatePotentialHazardsError = '[Listing] Listing Update Potential Hazards Error',
  ListingUpdateLocation = '[Listing] Listing Update Location',
  ListingUpdateLocationSuccess = '[Listing] Listing Update Location Success',
  ListingUpdateLocationFailed = '[Listing] Listing Update Location Failed',
  ListingUpdatePolicies = '[Listing] Listing Update Policies',
  ListingUpdatePoliciesSuccess = '[Listing] Listing Update Policies Success',
  ListingUpdatePoliciesFailed = '[Listing] Listing Update Policies Failed',
  ListingUpdateBookingPolicy = '[Listing] Listing Update Booking Policy',
  ListingUpdateBookingPolicySuccess = '[Listing] Listing Update Booking Policy Success',
  ListingUpdateBookingPolicyFailed = '[Listing] Listing Update Booking Policy Failed',
  ListingUpdateExtras = '[Listing] Listing Update Extras',
  ListingUpdateExtrasSuccess = '[Listing] Listing Update Extras Success',
  ListingUpdateExtrasFailed = '[Listing] Listing Update Extras Failed',
  ListingUpdateBlackoutTimes = '[Listing] Listing Update Blackout Times',
  ListingUpdateBlackoutTimesSuccess = '[Listing] Listing Update Blackout Times Success',
  ListingUpdateBlackoutTimesFailed = '[Listing] Listing Update Blackout Times Failed',
  ListingUpdatePricing = '[Listing] Update Price',
  ListingUpdatePricingSuccess = '[Listing] Update Price Success',
  ListingUpdatePricingFailed = '[Listing] Update Price Failed',
  ListingAddNewSite = '[Listing] Add New Site',
  ListingAddNewSiteSuccess = '[Listing] Add New Site Success',
  ListingAddNewSiteFailed = '[Listing] Add New Site Failed',
}

export const init = createAction(ListingActionTypes.ListingInit);

export const createListing = createAction(ListingActionTypes.CreateListing, props<{ listing: IListing }>());

export const createActivity = createAction(ListingActionTypes.CreateActivity, props<{ listing: IListing }>());

export const createAmenity = createAction(ListingActionTypes.CreateAmenity, props<{ listing: IListing }>());

export const createPolicy = createAction(ListingActionTypes.CreatePolicy, props<{ listing: IListing }>());

export const createSignalReception = createAction(
  ListingActionTypes.CreateSignalReception,
  props<{ listing: IListing }>(),
);

export const createListingFailed = createAction(ListingActionTypes.CreateListingFailed, props<{ error: string }>());

export const loadListing = createAction(ListingActionTypes.LoadListing, props<{ listingId: number | string }>());

export const loadListings = createAction(ListingActionTypes.LoadListings, props<{ payload?: IListingWhere }>());

export const clearListings = createAction(ListingActionTypes.ClearListings);

export const loadListingFailed = createAction(ListingActionTypes.LoadListingFailed, props<{ error: string }>());

export const delayedUploadListingImages = createAction(
  ListingActionTypes.DelayedUploadListingImages,
  props<{ formData: { [syncId: string]: FormData[] } }>(),
);

export const uploadListingImage = createAction(
  ListingActionTypes.UploadListingImage,
  props<{ formData: FormData; data: { listingId?: number; syncId?: string } }>(),
);

export const uploadBoondockReviewImage = createAction(
  ListingActionTypes.UploadBoondockReviewImage,
  props<{ formData: FormData; data: { listingId: number } }>(),
);

export const boondockReviewImageUploaded = createAction(
  ListingActionTypes.BoondockReviewImageUploaded,
  props<{ id: number; photos: IPhoto[] }>(),
);

export const boondockReviewImageUploadFailed = createAction(
  ListingActionTypes.BoondockReviewImageUploadFailed,
  props<{ error: string }>(),
);

export const removeImageFromListing = createAction(
  ListingActionTypes.RemoveImageFromListing,
  props<{ listingId: number; index: number }>(),
);

export const removeListing = createAction(ListingActionTypes.RemoveListing, props<{ listingId: number | string }>());

export const removeListingFailed = createAction(ListingActionTypes.RemoveListingFailed, props<{ error: string }>());

export const restoreListing = createAction(ListingActionTypes.RestoreListing, props<{ listingId: number }>());

export const removeActivity = createAction(
  ListingActionTypes.RemoveActivity,
  props<{ listing: IListing; activity: Activity }>(),
);

export const removeAmenity = createAction(
  ListingActionTypes.RemoveAmenity,
  props<{ listing: IListing; amenity: Amenity }>(),
);

export const removePolicy = createAction(
  ListingActionTypes.RemovePolicy,
  props<{ listing: IListing; policy: Policy }>(),
);

export const removeSignalReception = createAction(
  ListingActionTypes.RemoveSignalReception,
  props<{ listing: IListing; signalReception: SignalReception }>(),
);

export const listingCreated = createAction(ListingActionTypes.ListingCreated, props<{ listing: IListing }>());

export const listingImageRemoved = createAction(ListingActionTypes.ListingImageRemoved, props<{ listing: IListing }>());

export const listingRemoved = createAction(ListingActionTypes.ListingRemoved, props<{ listing: IListing }>());

export const listingRestored = createAction(ListingActionTypes.ListingRestored, props<{ listing: IListing }>());

export const listingLoaded = createAction(ListingActionTypes.ListingLoaded, props<{ listing: IListing }>());

export const listingsLoaded = createAction(ListingActionTypes.ListingsLoaded, props<{ listings: IListing[] }>());

export const listingUpdated = createAction(ListingActionTypes.ListingUpdated, props<{ listing: IListing }>());

export const updateListing = createAction(ListingActionTypes.UpdateListing, props<{ listing: IListing }>());

export const updateListingOffline = createAction('[Listing Offline] Update', props<{ payload: IListing }>());

export const processPendingListing = createAction(
  '[Listing Offline] Save Offline Listing To Server',
  props<{ listing: IListing }>(),
);

export const processPendingListingSuccess = createAction(
  '[Listing Offline] Process Pending Listing Upload Success',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props<{ listing: IListing }>(),
);

export const processPendingListingFailed = createAction(
  '[Listing Offline] Process Pending Listing Upload Failure',
  props<{ error: HttpErrorResponse; listing?: IListing }>(),
);

export const processPendingListingPhoto = createAction(
  '[Listing Offline] Process Pending Listing Image Upload',
  props<{ listingId: number; photo: IPhoto }>(),
);

export const processPendingListingPhotoSuccess = createAction(
  '[Listing Offline] Process Pending Listing Image Upload Success',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props<{ listingId: number; photo: IPhoto }>(),
);

export const processPendingListingPhotoFailed = createAction(
  '[Listing Offline] Process Pending Listing Image Upload Failure',
  props<{ error: any; listingId?: number; photo?: IPhoto }>(),
);

export const updateListingFailed = createAction(ListingActionTypes.UpdateListingFailed, props<{ error: string }>());

export const listingImagesUpdated = createAction(
  ListingActionTypes.ListingImagesUpdated,
  props<{ id?: number; syncId?: string; photos: IPhoto[] }>(),
);

export const enableListing = createAction(ListingActionTypes.EnableListing, props<{ listing: IListing }>());

export const enableAndHideListing = createAction(
  ListingActionTypes.EnableAndHideListing,
  props<{ listing: IListing }>(),
);

export const listingEnabled = createAction(ListingActionTypes.ListingEnabled, props<{ listing: IListing }>());

export const listingEnabledFailed = createAction(
  ListingActionTypes.ListingEnabledFailed,
  props<{ body: string; listingId?: number }>(),
);

export const disableListing = createAction(ListingActionTypes.DisableListing, props<{ listing: IListing }>());

export const listingDisabled = createAction(ListingActionTypes.ListingDisabled, props<{ listing: IListing }>());

export const listingDisabledFailed = createAction(ListingActionTypes.ListingDisabledFailed, props<{ error: string }>());

export const selectListing = createAction(ListingActionTypes.SelectListing, props<{ listingId?: number | string }>());

export const selectListingByListing = createAction(
  ListingActionTypes.SelectListingByListing,
  props<{ listing?: IListing }>(),
);

export const listingSelected = createAction(ListingActionTypes.ListingSelected, props<{ listing?: IListing }>());

export const favoriteListing = createAction(
  ListingActionTypes.FavoriteListing,
  props<{ point: IListing }>(),
);

export const unfavoriteListing = createAction(
  ListingActionTypes.UnfavoriteListing,
  props<{ point: IListing }>(),
);

export const listingFavorCompleted = createAction(
  ListingActionTypes.ListingFavorCompleted,
  props<{ id: number; favored: boolean }>(),
);

export const listingFavorFailed = createAction(ListingActionTypes.ListingFavorFailed, props<{ error: string }>());

export const recropImage = createAction(
  ListingActionTypes.ListingRecropImage,
  props<{ listingToSave: IListing; photo: ISubPhoto }>(),
);

export const recropImageFailure = createAction(
  ListingActionTypes.ListingRecropImageFailure,
  props<{ error: string }>(),
);

export const updateSelectedListingSiteObject = createAction(
  ListingActionTypes.UpdateSelectedListingSiteObject,
  props<{ site: ISite }>(),
);

export const updateDescription = createAction(
  ListingActionTypes.ListingUpdateDescription,
  props<{ listingId: number; data: IDtoSiteName }>(),
);

export const updateDescriptionSuccess = createAction(
  ListingActionTypes.ListingUpdateDescriptionSuccess,
  props<{ listing: IListing }>(),
);

export const updateDescriptionFailed = createAction(
  ListingActionTypes.ListingUpdateDescriptionError,
  props<{ error: any }>(),
);

export const updatePropertyTypes = createAction(
  ListingActionTypes.ListingUpdatePropertyTypes,
  props<{ listingId: number; types: ILandType['type'][] }>(),
);

export const updatePropertyTypesSuccess = createAction(
  ListingActionTypes.ListingUpdatePropertyTypesSuccess,
  props<{ listing: IListing }>(),
);

export const updatePropertyTypesFailed = createAction(
  ListingActionTypes.ListingUpdatePropertyTypesError,
  props<{ error: any }>(),
);

export const updatePotentialHazards = createAction(
  ListingActionTypes.ListingUpdatePotentialHazards,
  props<{ listingId: number; potentialHazards: boolean; potentialHazardDescription: string }>(),
);

export const updatePotentialHazardsSuccess = createAction(
  ListingActionTypes.ListingUpdatePotentialHazardsSuccess,
  props<{ listing: IListing }>(),
);

export const updatePotentialHazardsFailed = createAction(
  ListingActionTypes.ListingUpdatePotentialHazardsError,
  props<{ error: any }>(),
);

export const updateLocation = createAction(
  ListingActionTypes.ListingUpdateLocation,
  props<{ listingId: number; data: IDtoListingLocation }>(),
);

export const updateLocationSuccess = createAction(
  ListingActionTypes.ListingUpdateLocationSuccess,
  props<{ listing: IListing }>(),
);

export const updateLocationFailed = createAction(
  ListingActionTypes.ListingUpdateLocationFailed,
  props<{ error: any }>(),
);

export const updatePolicies = createAction(
  ListingActionTypes.ListingUpdatePolicies,
  props<{ listingId: number; policies: IPolicy[] }>(),
);

export const updatePoliciesSuccess = createAction(
  ListingActionTypes.ListingUpdatePoliciesSuccess,
  props<{ listing: IListing }>(),
);

export const updatePoliciesFailed = createAction(
  ListingActionTypes.ListingUpdatePoliciesFailed,
  props<{ error: any }>(),
);

export const updateBookingPolicy = createAction(
  ListingActionTypes.ListingUpdateBookingPolicy,
  props<{ listingId: number; data: IDtoListingBooking }>(),
);

export const updateBookingPolicySuccess = createAction(
  ListingActionTypes.ListingUpdateBookingPolicySuccess,
  props<{ listing: IListing }>(),
);

export const updateBookingPolicyFailed = createAction(
  ListingActionTypes.ListingUpdateBookingPolicyFailed,
  props<{ error: any }>(),
);

export const updateExtras = createAction(
  ListingActionTypes.ListingUpdateExtras,
  props<{ listingId: number; extras: IListingExtra[] }>(),
);

export const updateExtrasSuccess = createAction(
  ListingActionTypes.ListingUpdateExtrasSuccess,
  props<{ listing: IListing }>(),
);

export const updateExtrasFailed = createAction(ListingActionTypes.ListingUpdateExtrasFailed, props<{ error: any }>());

export const updateBlackoutTimes = createAction(
  ListingActionTypes.ListingUpdateBlackoutTimes,
  props<{ listingId: number; blackoutTimes: IDtoListingBlackoutTimes }>(),
);

export const updateBlackoutTimesSuccess = createAction(
  ListingActionTypes.ListingUpdateBlackoutTimesSuccess,
  props<{ listing: IListing }>(),
);

export const updateBlackoutTimesFailed = createAction(
  ListingActionTypes.ListingUpdateBlackoutTimesFailed,
  props<{ error: any }>(),
);

export const updatePricing = createAction(
  ListingActionTypes.ListingUpdatePricing,
  props<{
    nextReservationDiscount?: number;
    weekdayDiscount?: number;
    cancellationPolicy?: ICancellationPolicyOption['option'];
  }>(),
);

export const updatePricingSuccess = createAction(
  ListingActionTypes.ListingUpdatePricingSuccess,
  props<{ listing: IListing }>(),
);

export const updatePricingFailed = createAction(ListingActionTypes.ListingUpdatePricingFailed, props<{ error: any }>());

export const addNewSite = createAction(ListingActionTypes.ListingAddNewSite, props<{ listing: IListing }>());

export const addNewSiteSuccess = createAction(
  ListingActionTypes.ListingAddNewSiteSuccess,
  props<{ listing: IListing }>(),
);

export const addNewSiteFailed = createAction(ListingActionTypes.ListingAddNewSiteFailed, props<{ error: any }>());
